<template>
  <div class="renew-modifier">
    <div v-if="isLoadingDevis" class="renew-state">
      <i class="fas fa-spinner fa-spin"></i>
      <p>Chargement du contrat...</p>
    </div>

    <div v-else-if="loadError" class="renew-state renew-state--error">
      <i class="fas fa-exclamation-triangle"></i>
      <h2>Lien invalide</h2>
      <p>{{ loadError }}</p>
      <router-link :to="`/n/${$route.params.id}`" class="cta-secondary">Retour</router-link>
    </div>

    <div v-else class="modifier-shell">
      <div class="modifier-topnav">
        <router-link :to="backUrl" class="back-btn" aria-label="Retour au renouvellement">
          <i class="fas fa-arrow-left"></i>
        </router-link>
        <h1 class="modifier-title">Modifier mon <span>renouvellement</span></h1>
        <p class="modifier-subtitle">Ajustez la durée ou vos garanties avant de payer.</p>
      </div>

      <div class="modifier-card">
        <div class="info-strip">
          <i class="fas fa-car-side"></i>
          <div>
            <b>{{ vehiculeLabel }}</b>
            <template v-if="immatriculation && immatriculation !== '-'"> — {{ immatriculation }}</template>
          </div>
        </div>

        <div class="section-title">Durée</div>
        <div class="pills">
          <button
            v-for="d in DUREES"
            :key="d.value"
            type="button"
            class="pill"
            :class="{ 'pill--selected': selectedDuree === d.value }"
            @click="selectedDuree = d.value"
          >
            {{ d.label }}
          </button>
        </div>

        <div class="section-title">Choix de garanties</div>
        <div class="options">
          <label
            v-for="g in availableGaranties"
            :key="g.value"
            class="option"
            :class="{ 'option--selected': selectedGaranties.includes(g.value), 'option--locked': g.value === RC_VALUE }"
          >
            <input
              type="checkbox"
              class="option__check"
              :checked="selectedGaranties.includes(g.value)"
              :disabled="g.value === RC_VALUE"
              @change="toggleGarantie(g.value, $event.target.checked)"
            />
            <span class="option__box"></span>
            <span class="option__label">
              {{ g.label }}
              <span v-if="g.value === RC_VALUE" class="option__hint">(obligatoire)</span>
            </span>
          </label>
        </div>
      </div>

      <p v-if="simError" class="sim-error">{{ simError }}</p>

      <div class="modifier-actions">
        <router-link :to="backUrl" class="btn-cancel">Annuler</router-link>
        <button type="button" class="btn-validate" :disabled="isSubmitting" @click="applyChanges">
          <template v-if="isSubmitting">
            <i class="fas fa-spinner fa-spin"></i>&nbsp;Calcul en cours...
          </template>
          <template v-else>
            <i class="fas fa-check"></i>&nbsp;Obtenir le devis
          </template>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import dayjs from 'dayjs';
import Api from '../../services/Api';
import store from '../../store/store';
import {
  DUREES,
  RC_VALUE,
  GARANTIES_AUTO,
  GARANTIES_MOTO,
  filterGarantiesAutoByAge,
  isValidDuree,
  detectSelectedGarantiesAuto,
  detectSelectedGarantiesMoto,
} from './renewGaranties';

export default {
  name: 'AutoRenewModifier',
  data() {
    return {
      DUREES,
      RC_VALUE,
      isLoadingDevis: true,
      loadError: null,
      newDevis: null,
      selectedDuree: 12,
      selectedGaranties: [RC_VALUE],
      isSubmitting: false,
      simError: null,
    };
  },
  computed: {
    backUrl() {
      return `/n/${this.$route.params.id}`;
    },
    firstVehicule() {
      if (!this.newDevis || !Array.isArray(this.newDevis.vehicules) || !this.newDevis.vehicules.length) return null;
      return this.newDevis.vehicules[0];
    },
    vehiculeLabel() {
      const v = this.firstVehicule;
      if (!v) return '-';
      return [v.marque, v.model || v.modele].filter(Boolean).join(' ');
    },
    immatriculation() {
      const v = this.firstVehicule;
      return v ? (v.immatriculation || '-') : '-';
    },
    isMoto() {
      const v = this.firstVehicule;
      return !!(v && v.categorie === '2_roues');
    },
    vehiculeAge() {
      const v = this.firstVehicule;
      return v && typeof v.age === 'number' ? v.age : null;
    },
    availableGaranties() {
      return this.isMoto ? GARANTIES_MOTO : filterGarantiesAutoByAge(this.vehiculeAge);
    },
  },
  methods: {
    toggleGarantie(value, checked) {
      if (value === RC_VALUE) return;
      const set = new Set(this.selectedGaranties);
      if (checked) set.add(value); else set.delete(value);
      set.add(RC_VALUE);
      this.selectedGaranties = Array.from(set);
    },
    async fetchDevis(code) {
      this.isLoadingDevis = true;
      this.loadError = null;
      try {
        const createdby = store.state && store.state.user ? store.state.user._id : undefined;
        const { data } = await Api().post('devis/getbyrenew', { code_renew: code, createdby });
        if (!data || data.message === 'non autorisation !' || !data.devis) {
          this.loadError = "Le lien n'est plus valide. Contactez Assuraf si besoin.";
          return;
        }
        this.newDevis = data.devis;
        this.hydrateFromDevis();
        this.hydrateFromQuery();
      } catch (err) {
        console.error('Erreur fetchDevis modifier:', err);
        this.loadError = 'Impossible de charger le contrat. Vérifiez votre lien.';
      } finally {
        this.isLoadingDevis = false;
      }
    },
    hydrateFromDevis() {
      const v = this.firstVehicule;
      if (!v || !Array.isArray(v.garanties)) return;
      this.selectedGaranties = this.isMoto
        ? detectSelectedGarantiesMoto(v.garanties)
        : detectSelectedGarantiesAuto(v.garanties);
    },
    hydrateFromQuery() {
      const q = this.$route.query || {};
      if (isValidDuree(q.duree)) this.selectedDuree = Number(q.duree);
      if (typeof q.garanties === 'string' && q.garanties.length) {
        const wanted = q.garanties.split(',').map(s => s.trim()).filter(Boolean);
        const allowed = new Set(this.availableGaranties.map(g => g.value));
        const filtered = wanted.filter(g => allowed.has(g));
        if (filtered.length) {
          if (!filtered.includes(RC_VALUE)) filtered.unshift(RC_VALUE);
          this.selectedGaranties = Array.from(new Set(filtered));
        }
      }
    },
    buildSimulatorPayload() {
      const v = this.firstVehicule || {};
      const vehicule = Object.assign({}, v, { garanties: this.selectedGaranties.slice() });
      const dateEffet = dayjs(this.newDevis.dateEcheance).add(1, 'day').format('YYYY-MM-DD');
      const dateEcheance = dayjs(dateEffet).add(this.selectedDuree, 'month').add(-1, 'day').format('YYYY-MM-DD');
      const societe = this.newDevis.partenaireid && this.newDevis.partenaireid.societe;
      return {
        branche: 'Automobile',
        vehicule: [vehicule],
        assureurs: societe ? [societe] : [],
        dateEffet,
        dateEcheance,
        dateSouscription: dayjs(new Date()).format('YYYY-MM-DD'),
        plateforme: 'Front',
      };
    },
    async applyChanges() {
      if (this.isSubmitting) return;
      this.isSubmitting = true;
      this.simError = null;
      try {
        const { data } = await Api().post('simulator/add', this.buildSimulatorPayload());
        const devisSim = data && data.result && data.result.devis && data.result.devis[0];
        if (!devisSim) throw new Error('Simulation vide');
        const cachePayload = {
          duree: this.selectedDuree,
          garanties: this.selectedGaranties.slice(),
          prime_ttc: devisSim.prime_ttc,
          prime_nette: devisSim.prime_nette,
          taxe: devisSim.taxe,
          accessoires: devisSim.accessoires,
        };
        sessionStorage.setItem(`renew_modif_${this.$route.params.id}`, JSON.stringify(cachePayload));
        this.$router.push({
          path: this.backUrl,
          query: { duree: this.selectedDuree, garanties: this.selectedGaranties.join(',') },
        });
      } catch (err) {
        console.error('Erreur simulation renouvellement:', err);
        this.simError = 'Impossible de calculer le devis. Réessayez.';
      } finally {
        this.isSubmitting = false;
      }
    },
  },
  mounted() {
    window.scrollTo(0, 0);
    const code = this.$route.params.id;
    if (code) {
      this.fetchDevis(code);
    } else {
      this.isLoadingDevis = false;
      this.loadError = 'Lien de renouvellement manquant.';
    }
  },
};
</script>

<style scoped>
.renew-modifier {
  background: linear-gradient(180deg, #ffa50992 0%, #e0f7fa 20%, #ffffff 35%, #ffffff 100%);
  min-height: 100vh;
  padding: 100px 16px 60px;
  font-family: 'Lato', 'Roboto', sans-serif;
  color: #2b2b2b;
}

.renew-state {
  max-width: 480px;
  margin: 80px auto;
  text-align: center;
  background: #fff;
  padding: 36px 24px;
  border-radius: 14px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);
}
.renew-state i { font-size: 42px; color: #ffa409; margin-bottom: 14px; }
.renew-state--error i { color: #dc2626; }
.renew-state h2 { margin: 0 0 8px; font-size: 20px; font-weight: 800; }
.renew-state p { margin: 0 0 18px; color: #6b7280; font-size: 14px; }
.cta-secondary {
  display: inline-block; background: #ffa409; color: #fff; text-decoration: none;
  padding: 10px 20px; border-radius: 8px; font-weight: 700;
}

.modifier-shell { max-width: 720px; margin: 0 auto; }

.modifier-topnav { position: relative; text-align: center; padding: 8px 0 20px; }
.back-btn {
  position: absolute; left: 0; top: 8px;
  background: rgba(255,255,255,.9); border: 0; width: 40px; height: 40px;
  border-radius: 50%; display: inline-grid; place-items: center; cursor: pointer;
  color: #111; text-decoration: none; font-size: 15px;
  box-shadow: 0 2px 6px rgba(0,0,0,.08);
}
.modifier-title { margin: 0 0 4px; font-size: 26px; font-weight: 800; }
.modifier-title span { color: #ffa409; text-shadow: 0 0 2px #fff; }
.modifier-subtitle { margin: 0; color: #191919; font-size: 14px; }

.modifier-card {
  background: #fff; border-radius: 14px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);
  margin-top: 24px; padding: 24px;
}

.info-strip {
  display: flex; align-items: center; gap: 10px;
  background: #f7f7f7; border-radius: 8px; padding: 10px 12px;
  font-size: 13px; color: #555; margin-bottom: 20px;
}
.info-strip i { color: #999; }
.info-strip b { color: #333; font-weight: 700; }

.section-title {
  font-size: 12px; font-weight: 800; letter-spacing: .5px; text-transform: uppercase;
  color: #666; margin: 16px 0 10px;
}

.pills { display: grid; grid-template-columns: repeat(3, 1fr); gap: 8px; }
.pill {
  padding: 12px 8px; border: 2px solid #eaeaea; border-radius: 12px;
  text-align: center; font-weight: 700; font-size: 14px; cursor: pointer;
  background: #fff; color: #2b2b2b; transition: border-color .15s ease, background .15s ease;
}
.pill:hover { border-color: #ffa409; }
.pill--selected { border-color: #ffa409; background: #fff5e5; color: #b8790a; }

.options { display: flex; flex-direction: column; gap: 8px; }
.option {
  display: flex; align-items: center; gap: 12px; padding: 12px;
  border: 2px solid #eaeaea; border-radius: 12px; cursor: pointer;
  background: #fff; text-align: left; transition: border-color .15s ease, background .15s ease;
}
.option:hover { border-color: #ffa409; }
.option--selected { border-color: #ffa409; background: #fff5e5; }
.option--locked { cursor: not-allowed; }
.option__check { position: absolute; opacity: 0; pointer-events: none; }
.option__box {
  width: 20px; height: 20px; border: 2px solid #ccc; border-radius: 4px;
  display: grid; place-items: center; flex-shrink: 0; background: #fff;
}
.option--selected .option__box { border-color: #ffa409; background: #ffa409; }
.option--selected .option__box::after {
  content: ''; width: 10px; height: 6px; border-left: 2px solid #fff; border-bottom: 2px solid #fff;
  transform: rotate(-45deg); margin-top: -2px;
}
.option__label { font-weight: 700; font-size: 14px; color: #2b2b2b; }
.option__hint { display: block; font-size: 11px; font-weight: 600; color: #999; margin-top: 2px; text-transform: none; letter-spacing: 0; }

.sim-error {
  color: #dc2626; font-size: 13px; font-weight: 700; text-align: center;
  margin: 12px 0 0;
}
.modifier-actions {
  display: flex; gap: 10px; justify-content: flex-end;
  margin-top: 20px; padding-bottom: 10px;
}
.btn-validate:disabled { opacity: .7; cursor: not-allowed; }
.btn-cancel {
  background: #fff; color: #666; border: 1px solid #ddd; padding: 12px 22px;
  border-radius: 10px; font-weight: 700; font-size: 14px; cursor: pointer;
  text-decoration: none; display: inline-flex; align-items: center;
}
.btn-validate {
  background: #ffa409; color: #fff; border: 0; padding: 12px 26px;
  border-radius: 10px; font-weight: 800; font-size: 14px; cursor: pointer;
  box-shadow: 0 4px 12px rgba(255, 164, 9, .35);
}

@media (max-width: 480px) {
  .modifier-title { font-size: 22px; }
  .modifier-actions { flex-direction: column-reverse; }
  .btn-cancel, .btn-validate { width: 100%; justify-content: center; text-align: center; }
}
</style>
